import { CheckIcon, CopyIcon } from 'lucide-react'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { useClipboard } from '@/utils/useClipboard'

interface CopyableContentTooltipProps {
  children: ReactNode
  content: {
    label: string
    textToCopy: string
  }[]
  className?: string
}

export function CopyableContentTooltip({ children, content, className }: CopyableContentTooltipProps) {
  const { copy } = useClipboard()
  const [copiedIndex, setCopiedIndex] = useState<number | undefined>()
  const resetTimeoutRef = useRef<NodeJS.Timeout>()

  async function handleCopy(_e: React.MouseEvent<HTMLButtonElement, MouseEvent>, contentIndex: number): Promise<void> {
    await copy(content[contentIndex]!.textToCopy)
    setCopiedIndex(contentIndex)
    clearTimeout(resetTimeoutRef.current)
    resetTimeoutRef.current = setTimeout(() => setCopiedIndex(undefined), 1500)
  }

  useEffect(() => {
    return () => clearTimeout(resetTimeoutRef.current)
  }, [])

  return (
    <Tooltip>
      <TooltipTrigger className={className}>{children}</TooltipTrigger>
      <TooltipContent className="flex flex-col gap-0">
        {content.map(({ label }, index) => (
          <div key={index} className="mt-0.5! flex items-center gap-1">
            <span>{label}</span>
            <div className="flex items-center justify-center">
              {copiedIndex === index ? (
                <CheckIcon className="icon-size-xxs icon-system-success-primary animate-reveal" />
              ) : (
                <button onClick={(e) => handleCopy(e, index)}>
                  <CopyIcon className="icon-size-xxs" />
                </button>
              )}
            </div>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}
